import React from 'react';
import { ArrowLeft, Zap, BarChart3, PieChart, TrendingUp, Activity, MessageSquare, Code, Cpu, Database } from 'lucide-react';
import { Link } from 'react-router-dom';

const AgenticDashboard = () => {
  const features = [
    {
      icon: MessageSquare,
      title: "Natural Language Queries",
      description: "Executives describe data needs in plain English, no technical knowledge required."
    },
    {
      icon: Code,
      title: "React Component Generation",
      description: "Instantly generates interactive, production-ready React visualization components."
    },
    {
      icon: Cpu,
      title: "AI-Powered Analysis",
      description: "Advanced analytics and trend identification powered by our Precision-RAG system."
    },
    {
      icon: Activity,
      title: "Real-time Data Integration",
      description: "Connects directly to your semantic lakehouse for live data insights."
    }
  ];

  const examples = [
    {
      prompt: "Compare our Q3 exposure in Emerging Markets against the 2024 volatility index.",
      type: "Risk Analysis",
      icon: TrendingUp,
      visualization: "Multi-line chart with volatility overlay",
      insights: "15% higher exposure in Brazil, 8% lower in India"
    },
    {
      prompt: "Show me revenue breakdown by product category for the last 4 quarters.",
      type: "Revenue Analysis",
      icon: PieChart,
      visualization: "Stacked bar chart with trend lines",
      insights: "Enterprise segment grew 23% YoY"
    },
    {
      prompt: "What's our loan portfolio performance by credit rating?",
      type: "Portfolio Analysis",
      icon: BarChart3,
      visualization: "Heatmap with performance metrics",
      insights: "AAA-rated loans performing 12% above baseline"
    },
    {
      prompt: "Compare compliance scores across all business units this month.",
      type: "Compliance Dashboard",
      icon: Activity,
      visualization: "Gauge charts with trend indicators",
      insights: "Investment banking needs attention on KYC compliance"
    }
  ];

  const capabilities = [
    {
      title: "Instant Generation",
      description: "From prompt to production dashboard in under 30 seconds",
      metric: "30s",
      color: "from-blue-600 to-purple-600"
    },
    {
      title: "Zero Coding Required",
      description: "Business users can create complex analytical dashboards",
      metric: "0 Lines",
      color: "from-green-600 to-teal-600"
    },
    {
      title: "Unlimited Variations",
      description: "Generate unlimited dashboard variations and iterations",
      metric: "∞",
      color: "from-orange-600 to-red-600"
    },
    {
      title: "Production Ready",
      description: "All generated components are enterprise-grade and secure",
      metric: "100%",
      color: "from-purple-600 to-pink-600"
    }
  ];

  const workflow = [
    {
      step: "1",
      title: "Natural Language Input",
      description: "Executive types request in plain English",
      icon: MessageSquare,
      color: "from-purple-600 to-pink-600"
    },
    {
      step: "2",
      title: "Intent Analysis",
      description: "AI analyzes request and identifies data requirements",
      icon: Cpu,
      color: "from-blue-600 to-purple-600"
    },
    {
      step: "3",
      title: "Data Retrieval",
      description: "Queries semantic lakehouse using Precision-RAG",
      icon: Database,
      color: "from-green-600 to-teal-600"
    },
    {
      step: "4",
      title: "Dashboard Generation",
      description: "Creates interactive React component with visualizations",
      icon: Code,
      color: "from-orange-600 to-red-600"
    }
  ];

  return (
    <div className="pt-16 min-h-screen bg-navy-950 text-white">
      <div className="container mx-auto px-6 lg:px-12 py-12">
        <Link to="/" className="inline-flex items-center text-orange-400 hover:text-orange-300 mb-8 transition-colors">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Home
        </Link>

        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 items-center mb-16">
            <div>
              <div className="inline-flex p-3 rounded-2xl bg-gradient-to-r from-[#f97316] to-[#fbce16] mb-6">
                <Zap className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-5">
                <span className="text-gradient-blue">Agentic Dashboard Generator</span>
              </h1>
              <h2 className="text-xl sm:text-2xl text-slate-300 mb-6">
                Prompt-to-Insight: The Future of Internal Reporting
              </h2>
              <p className="text-lg text-slate-400 leading-relaxed mb-8">
                Static dashboards take weeks for IT teams to build. Our Product UI allows executives to
                describe the data they need in plain English.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/#demo"
                  className="inline-flex items-center justify-center px-7 py-3 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white font-semibold rounded-lg hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:scale-[1.02]"
                >
                  Request Demo
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center px-7 py-3 border border-slate-700/50 text-white font-semibold rounded-lg hover:bg-white/5 transition-all duration-300"
                >
                  View All Solutions
                </Link>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#f97316]/20 to-[#fbce16]/20 rounded-3xl blur-2xl" />
              <div className="relative rounded-2xl overflow-hidden border border-slate-700/40 shadow-2xl shadow-orange-900/20">
                <img
                  src="/solution4_dashboard.png"
                  alt="Agentic dashboard generator"
                  className="w-full h-auto object-cover max-h-[340px] sm:max-h-[420px] lg:max-h-[520px]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/45 via-transparent to-transparent pointer-events-none" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 rounded-2xl p-8 mb-16 border border-slate-700/40 premium-border premium-elevate">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4 text-white">The Traditional Problem</h3>
              <p className="text-lg text-slate-300 leading-relaxed mb-6">
                Building dashboards traditionally involves multiple stakeholders, lengthy development cycles, 
                and constant iterations. Business users wait weeks for IT teams to understand requirements, 
                develop code, and deploy solutions—only to find they need modifications.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                  <p className="text-3xl font-bold text-purple-400">3-4 weeks</p>
                  <p className="text-slate-300">Average dashboard development time</p>
                </div>
                <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                  <p className="text-3xl font-bold text-pink-400">5+ stakeholders</p>
                  <p className="text-slate-300">Typical approval chain</p>
                </div>
                <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                  <p className="text-3xl font-bold text-red-400">70%</p>
                  <p className="text-slate-300">Require post-deployment changes</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-900/20 to-teal-900/20 rounded-2xl p-8 mb-16 border border-slate-700/40 premium-border premium-elevate">
            <div className="flex items-start space-x-4">
              <Zap className="w-8 h-8 text-green-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-bold mb-4 text-white">The BeeMind Solution</h3>
                <p className="text-lg text-slate-300 leading-relaxed mb-6">
                  Our Product UI allows executives to describe the data they need in plain English.
                </p>
                <div className="bg-black/50 rounded-lg p-6 border border-slate-700/40 premium-subcard premium-elevate mb-6">
                  <p className="text-lg font-semibold text-green-400 mb-2">Example Prompt:</p>
                  <p className="text-xl text-white italic">
                    "Compare our Q3 exposure in Emerging Markets against the 2024 volatility index."
                  </p>
                </div>
                <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 premium-subcard premium-elevate">
                  <p className="text-lg font-semibold text-green-400 mb-2">Action:</p>
                  <p className="text-slate-300">
                    The BeeMind Agent queries the Data Lake, summarizes the trend, and instantly generates 
                    a React-based visualization component on the fly.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Core Features</h3>
            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="bg-navy-800 rounded-xl p-6 border border-slate-700/40 premium-border premium-elevate hover:border-purple-500 transition-all duration-300">
                    <div className="flex items-start space-x-4">
                      <div className="inline-flex p-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="text-xl font-bold mb-2 text-white">{feature.title}</h4>
                        <p className="text-slate-400 leading-relaxed">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Example Dashboard Generations</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {examples.map((example, index) => {
                const Icon = example.icon;
                return (
                  <div key={index} className="bg-navy-800 rounded-xl p-6 border border-slate-700/40 hover:border-purple-500 transition-all duration-300">
                    <div className="flex items-start space-x-4 mb-4">
                      <div className="inline-flex p-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <span className="inline-block px-3 py-1 bg-purple-900/50 text-purple-400 rounded-full text-sm mb-2">
                          {example.type}
                        </span>
                        <h4 className="text-lg font-bold text-white mb-2">Generated Dashboard</h4>
                      </div>
                    </div>
                    <div className="bg-black/50 rounded-lg p-4 border border-slate-700/40 mb-4">
                      <p className="text-sm text-slate-400 mb-2">Prompt:</p>
                      <p className="text-white italic">"{example.prompt}"</p>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span className="text-sm text-slate-300">Visualization: {example.visualization}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                        <span className="text-sm text-slate-300">Key Insights: {example.insights}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Agentic Workflow</h3>
            <div className="grid md:grid-cols-4 gap-6">
              {workflow.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="relative">
                    <div className="bg-navy-800 rounded-xl p-6 border border-slate-700/40 hover:border-purple-500 transition-all duration-300">
                      <div className={`inline-flex p-3 rounded-lg bg-gradient-to-r ${step.color} mb-4`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                        {step.step}
                      </div>
                      <h4 className="text-lg font-bold mb-2 text-white">{step.title}</h4>
                      <p className="text-slate-400 text-sm leading-relaxed">{step.description}</p>
                    </div>
                    {index < workflow.length - 1 && (
                      <div className="hidden md:block absolute top-1/2 -right-3 w-6 h-0.5 bg-slate-700 transform -translate-y-1/2"></div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Platform Capabilities</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {capabilities.map((capability, index) => (
                <div key={index} className="bg-navy-800 rounded-xl p-6 text-center border border-slate-700/40">
                  <div className={`inline-flex p-4 rounded-full bg-gradient-to-r ${capability.color} mb-4`}>
                    <span className="text-2xl font-bold text-white">{capability.metric}</span>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{capability.title}</h4>
                  <p className="text-sm text-slate-400">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-3xl font-bold mb-8">Transform Your Reporting</h3>
            <p className="text-xl text-slate-400 mb-8">
              Move from weeks-long development cycles to instant dashboard generation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/#demo"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#f97316] to-[#fbce16] text-white font-semibold rounded-lg hover:from-[#fb7a1f] hover:to-[#fbd32b] transition-all duration-300 transform hover:scale-105"
              >
                Schedule Live Demo
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center px-8 py-4 border border-slate-700/40 text-white font-semibold rounded-lg hover:bg-navy-800 transition-all duration-300"
              >
                View Other Services
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgenticDashboard;
